import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Home, Menu, X, User } from "lucide-react";

const Header = ({ onAuthClick }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const navLinks = [
    { label: "Properties", href: "#properties" },
    { label: "How It Works", href: "#blockchain" },
    { label: "About", href: "#about" },
    { label: "Pricing", href: "#pricing" },
    { label: "FAQ", href: "#faq" },
    { label: "Contact", href: "#contact" },
  ];

  const handleNavClick = (href) => {
    setMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleLogout = async () => {
    await logout();
    setMobileMenuOpen(false);
  };

  return (
    <header 
      className="sticky top-0 z-50 glass border-b border-slate-200/50"
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#C04000] rounded-lg flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span 
              className="text-xl font-bold text-slate-900" 
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="logo"
            >
              MyDoor360
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Desktop Auth Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            {user ? (
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate("/dashboard")}
                  className="text-slate-600 hover:text-slate-900"
                  data-testid="dashboard-nav-button"
                >
                  <User className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  className="border-slate-200 text-slate-700 hover:bg-slate-50"
                  data-testid="logout-nav-button"
                >
                  Logout
                </Button>
              </div>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => onAuthClick("login")}
                  className="text-slate-600 hover:text-slate-900"
                  data-testid="login-button"
                >
                  Sign In
                </Button>
                <Button
                  onClick={() => onAuthClick("register")}
                  className="bg-[#C04000] hover:bg-[#A03500] text-white"
                  data-testid="get-started-button"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-slate-600 hover:text-slate-900"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-200" data-testid="mobile-menu">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={() => handleNavClick(link.href)}
                  className="text-left py-2 text-slate-600 hover:text-slate-900 font-medium"
                >
                  {link.label}
                </button>
              ))}
              <div className="pt-4 border-t border-slate-200 mt-2 flex flex-col gap-2">
                {user ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => { navigate("/dashboard"); setMobileMenuOpen(false); }}
                      className="w-full justify-center"
                    >
                      Dashboard
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={handleLogout}
                      className="w-full justify-center"
                    >
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => { onAuthClick("login"); setMobileMenuOpen(false); }}
                      className="w-full justify-center"
                    >
                      Sign In
                    </Button>
                    <Button
                      onClick={() => { onAuthClick("register"); setMobileMenuOpen(false); }}
                      className="w-full justify-center bg-[#C04000] hover:bg-[#A03500] text-white"
                    >
                      Get Started
                    </Button>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
